import { PureComponent, Fragment } from 'react';

import { ErrorState } from '@atlassian/bitbucket-generic-message';

import * as styles from './pageable-table.style';
import TransparentLoadingCover from './transparent-loading-cover';

export type PageableTableProps<Row> = {
  children: (row: Row, index: number) => JSX.Element;
  renderEmptyState: () => JSX.Element;
  onClickErrorState?: () => void;
  isError: boolean;
  isLoading: boolean;
  renderAnnouncer?: (() => JSX.Element) | null;
  renderBeforeRows?: (() => JSX.Element) | null | undefined;
  renderColumnDefinitions: () => JSX.Element;
  renderHeaders?: (() => JSX.Element) | null;
  renderPagination?: (() => JSX.Element) | null;
  rows: Row[];
  shouldShowLoadingCover: boolean;
  shouldUseTableLayoutAuto?: boolean;
  // NOTE: Passing the SentryID is for an for an in-flight project.
  // Please do not use until ATLAS-89472 has shipped.
  sentryId?: string;
};

export default class PageableTable<Row> extends PureComponent<
  PageableTableProps<Row>
> {
  static defaultColumnDefinitions = () => null;

  static defaultEmptyState = () => null;

  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    renderEmptyState: PageableTable.defaultEmptyState,
    isError: false,
    isLoading: false,
    renderBeforeRows: null,
    renderColumnDefinitions: PageableTable.defaultColumnDefinitions,
    renderHeaders: null,
    renderPagination: null,
    rows: [],
    shouldShowLoadingCover: true,
  };

  renderTableRows() {
    const { children, rows, renderBeforeRows } = this.props;

    return (
      <Fragment>
        {renderBeforeRows && renderBeforeRows()}
        {rows.map(children)}
      </Fragment>
    );
  }

  renderTable() {
    const {
      renderEmptyState,
      isLoading,
      rows,
      renderColumnDefinitions,
      renderHeaders,
      renderPagination,
      shouldUseTableLayoutAuto,
    } = this.props;

    const shouldRenderEmptyState = !isLoading && !rows.length;

    return (
      <Fragment>
        <styles.Table
          isLoading={isLoading}
          shouldUseTableLayoutAuto={shouldUseTableLayoutAuto}
        >
          {renderColumnDefinitions()}
          {renderHeaders && <thead>{renderHeaders()}</thead>}
          <styles.TableBody showHeaders={!!renderHeaders}>
            {shouldRenderEmptyState
              ? renderEmptyState()
              : this.renderTableRows()}
          </styles.TableBody>
        </styles.Table>
        {renderPagination && renderPagination()}
      </Fragment>
    );
  }

  render() {
    const {
      isError,
      isLoading,
      onClickErrorState,
      shouldShowLoadingCover,
      renderAnnouncer,
      sentryId,
    } = this.props;

    if (isError) {
      return <ErrorState onClick={onClickErrorState} sentryId={sentryId} />;
    }

    return (
      <styles.Container>
        {renderAnnouncer && renderAnnouncer()}
        {shouldShowLoadingCover && isLoading && <TransparentLoadingCover />}
        {this.renderTable()}
      </styles.Container>
    );
  }
}
